import { Link } from "react-router-dom";
import { Facebook, Instagram, Mail, MapPin, Phone, Star } from "lucide-react";
import { BUSINESS, IMAGES } from "../data/business";

export default function Footer() {
  return (
    <footer
      className="bg-[var(--beautix-cream)] border-t border-[var(--beautix-line)] pt-20 pb-10 mt-24"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-4 gap-14">
        <div className="md:col-span-1 space-y-6">
          <img
            src={IMAGES.logo}
            alt="BEAUTIX logo — salon kosmetyczny Katowice Ligota"
            className="h-20 md:h-24 w-auto"
          />
          <p className="text-sm text-[var(--beautix-muted)] leading-relaxed max-w-xs">
            Nowoczesne studio kosmetyki w Katowicach. Stylizacja paznokci, profesjonalna
            podologia i kompleksowe zabiegi kosmetyczne twarzy — pod jednym dachem.
          </p>
          <div className="flex gap-3">
            <a
              href={BUSINESS.social.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 border border-[var(--beautix-line)] rounded-full text-[var(--beautix-ink)] hover:text-[var(--beautix-pink)] hover:border-[var(--beautix-pink)] transition-colors"
              aria-label="BEAUTIX Facebook"
              data-testid="footer-facebook"
            >
              <Facebook className="w-4 h-4" strokeWidth={1.5} />
            </a>
            <a
              href={BUSINESS.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 border border-[var(--beautix-line)] rounded-full text-[var(--beautix-ink)] hover:text-[var(--beautix-pink)] hover:border-[var(--beautix-pink)] transition-colors"
              aria-label="BEAUTIX Instagram"
              data-testid="footer-instagram"
            >
              <Instagram className="w-4 h-4" strokeWidth={1.5} />
            </a>
            <a
              href={BUSINESS.social.googleMaps}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 border border-[var(--beautix-line)] rounded-full text-[var(--beautix-ink)] hover:text-[var(--beautix-pink)] hover:border-[var(--beautix-pink)] transition-colors"
              aria-label="BEAUTIX w Google Maps — zobacz opinie i lokalizację"
              data-testid="footer-google-maps"
            >
              <Star className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </div>
        </div>

        <div>
          <p className="eyebrow mb-5">Usługi</p>
          <ul className="space-y-3 text-sm">
            <li>
              <Link to="/stylizacja-paznokci-katowice" className="link-underline">
                Stylizacja paznokci Katowice
              </Link>
            </li>
            <li>
              <Link to="/podologia-katowice" className="link-underline">
                Gabinet podologiczny Katowice
              </Link>
            </li>
            <li>
              <Link to="/kosmetyka-twarzy-katowice" className="link-underline">
                Kosmetyka twarzy Katowice
              </Link>
            </li>
            <li>
              <Link to="/cennik" className="link-underline">
                Cennik
              </Link>
            </li>
            <li>
              <Link to="/blog" className="link-underline">
                Blog SEO
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <p className="eyebrow mb-5">Kontakt</p>
          <ul className="space-y-4 text-sm">
            <li className="flex gap-3">
              <MapPin className="w-4 h-4 mt-0.5 text-[var(--beautix-gold)] shrink-0" strokeWidth={1.5} />
              <span>
                {BUSINESS.address.street}
                <br />
                {BUSINESS.address.postalCode} {BUSINESS.address.city}
              </span>
            </li>
            <li className="flex gap-3">
              <Phone className="w-4 h-4 mt-0.5 text-[var(--beautix-gold)] shrink-0" strokeWidth={1.5} />
              <div className="flex flex-col gap-1">
                <a
                  href={`tel:${BUSINESS.phones[1].tel}`}
                  className="link-underline"
                  data-testid="footer-mobile-phone"
                >
                  {BUSINESS.phones[1].number}
                </a>
                <a
                  href={`tel:${BUSINESS.phones[0].tel}`}
                  className="link-underline text-[var(--beautix-muted)]"
                >
                  {BUSINESS.phones[0].number}
                </a>
              </div>
            </li>
            <li className="flex gap-3">
              <Mail className="w-4 h-4 mt-0.5 text-[var(--beautix-gold)] shrink-0" strokeWidth={1.5} />
              <a
                href={`mailto:${BUSINESS.email}`}
                className="link-underline break-all"
                data-testid="footer-email"
              >
                {BUSINESS.email}
              </a>
            </li>
          </ul>
        </div>

        <div>
          <p className="eyebrow mb-5">Godziny otwarcia</p>
          <ul className="space-y-3 text-sm">
            {BUSINESS.hours.map((h) => (
              <li key={h.day} className="flex justify-between gap-4">
                <span>{h.day}</span>
                <span className="text-[var(--beautix-muted)]">{h.value}</span>
              </li>
            ))}
          </ul>
          <p className="eyebrow mt-8 mb-3">Wyróżnienia</p>
          <p className="text-xs text-[var(--beautix-muted)] leading-relaxed">
            Laureat konkursu <span className="text-[var(--beautix-gold)]">Złota Firma</span> 2022 – 2026.
            Ocena 4,9/5 na podstawie 117 opinii Google, Facebook, panoramafirm.pl.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 mt-16 pt-8 border-t border-[var(--beautix-line)] flex flex-col md:flex-row justify-between gap-3 text-xs text-[var(--beautix-muted)]">
        <p>© {new Date().getFullYear()} {BUSINESS.name}. Wszelkie prawa zastrzeżone.{" "}
          <Link to="/polityka-prywatnosci" className="link-underline ml-2" data-testid="footer-privacy">
            Polityka prywatności
          </Link>
        </p>
        <p>
          Salon urody Katowice Ligota • Stylizacja paznokci • Podologia • Kosmetyka twarzy
        </p>
      </div>
    </footer>
  );
}
